import { Box, Dialog, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { EnumUserType, User } from '../../../../lib/graphql';

import { CustomerForm } from '../customer-form/customer-form';

/**
 * Standalone add/edit customer dialog.
 *
 * Every top-level caller now opens this through the modal provider instead —
 * see `useAddCustomerModal`. This raw Dialog survives for exactly one caller:
 * `CustomerForm`'s nested "Add Producer" flow, which opens a SECOND customer
 * form on top of an already-open one. `ModalProvider` holds a single modal at a
 * time, so routing the nested one through it would replace the outer form and
 * discard whatever the user had typed into it. It also has to stay a lazy
 * import there, to break the customer-form ↔ add-customer-dialog cycle.
 *
 * Do not add new call sites — use `useAddCustomerModal`.
 */

export interface AddCustomerDialogProps {
  customer?: User | null;
  forcedType?: EnumUserType;
  onSuccess?: (created: User) => void | Promise<void> | undefined;
  open: boolean;
  onClose: () => void;
  titleOverride?: string;
  vendorId?: string;
}

export default function AddCustomerDialog({
  customer = null,
  forcedType = undefined,
  onSuccess = undefined,
  open,
  onClose,
  titleOverride = 'Add Customer',
  vendorId = undefined,
}: AddCustomerDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-customer-title"
      aria-describedby="add-customer-description"
      slotProps={{ paper: { sx: { p: 3 } } }}
    >
      {/* Visually hidden description for assistive technology */}
      <Box
        id="add-customer-description"
        component="p"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Fill out the customer details and use Save to create a new customer or
        Cancel to close this dialog.
      </Box>
      <Box display="flex" justifyContent="space-between" sx={{ pb: 2 }}>
        <DialogTitle id="add-customer-title" sx={{ p: 0 }}>
          {titleOverride}
        </DialogTitle>
        <IconButton onClick={onClose} aria-label="Close dialog" sx={{ p: 0 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <CustomerForm
        customer={customer}
        forcedType={forcedType}
        onSuccess={onSuccess}
        onCancel={onClose}
        onClose={onClose}
        isInDialog
        showActions
        idPrefix="add-customer"
        vendorId={vendorId}
      />
    </Dialog>
  );
}
